/**
 * Vlastní programy uživatele: ŘAZENÍ, FILTR a HLEDÁNÍ tras.
 * Tři režimy tvorby s automatickou konverzí směrem nahoru:
 *   vizuální skládačka → výrazový jazyk → JavaScript.
 * (Opačný směr obecně nelze — JS je silnější; UI to vysvětluje.)
 * Spouští se v sandbox workeru s timeoutem (viz programRun.ts).
 */
import { compileExpression, validateExpression, type ProgramKind } from './programLang';

export type ProgramMode = 'visual' | 'expr' | 'js';

export interface VisualFilterRow {
  field: string; // identifikátor jazyka (cas, cena, …)
  op: '<' | '<=' | '>' | '>=' | '=' | '!=';
  value: string; // číslo / true / false / "text"
}

export interface VisualSortKey {
  field: string;
  desc: boolean;
}

export interface VisualSpec {
  /** Spojka řádků filtru. */
  join: 'A' | 'NEBO';
  filter: VisualFilterRow[];
  sort: VisualSortKey[];
  /** Podmínka KDYŽ pro hledání (jen ctx pole: hodina/den/vikend). */
  when: VisualFilterRow | null;
}

export interface CustomProgram {
  id: string;
  kind: ProgramKind;
  name: string;
  mode: ProgramMode;
  visual: VisualSpec;
  expr: string;
  js: string;
}

let progSeq = 0;

export function createProgram(kind: ProgramKind, name?: string): CustomProgram {
  progSeq += 1;
  const tpl: Record<ProgramKind, Partial<CustomProgram>> = {
    filter: { expr: 'cas < 60 A cena < 100', visual: { join: 'A', filter: [{ field: 'cas', op: '<', value: '60' }], sort: [], when: null } },
    sort: { expr: 'PODLE cas + cekani*2, cena', visual: { join: 'A', filter: [], sort: [{ field: 'cas', desc: false }], when: null } },
    search: {
      expr: 'KDYZ vikend: FILTR bezAuta; RAZENI PODLE komfort SESTUPNE KONEC\nFILTR cas < 90; RAZENI PODLE cas',
      visual: { join: 'A', filter: [{ field: 'cas', op: '<', value: '90' }], sort: [{ field: 'cas', desc: false }], when: { field: 'vikend', op: '=', value: 'true' } },
    },
  };
  const base = tpl[kind];
  return {
    id: `prog-${Date.now().toString(36)}-${progSeq}`,
    kind,
    name: name ?? (kind === 'filter' ? 'Můj filtr' : kind === 'sort' ? 'Moje řazení' : 'Moje hledání'),
    mode: 'visual',
    visual: base.visual as VisualSpec,
    expr: base.expr as string,
    js: '',
  };
}

/** Vizuální skládačka → výrazový jazyk (konverze 1→2). */
export function visualToExpr(p: CustomProgram): string {
  const v = p.visual;
  const row = (r: VisualFilterRow): string => `${r.field} ${r.op} ${r.value}`;
  const filterExpr = v.filter.length > 0 ? v.filter.map(row).join(` ${v.join} `) : '';
  const sortExpr = v.sort.length > 0 ? 'PODLE ' + v.sort.map((k) => `${k.field}${k.desc ? ' SESTUPNE' : ''}`).join(', ') : '';
  if (p.kind === 'filter') return filterExpr || 'cas >= 0';
  if (p.kind === 'sort') return sortExpr || 'PODLE cas';
  const body = [filterExpr ? `FILTR ${filterExpr}` : '', sortExpr ? `RAZENI ${sortExpr}` : ''].filter(Boolean).join('; ');
  if (v.when) return `KDYZ ${row(v.when)}: ${body || 'RAZENI PODLE cas'} KONEC`;
  return body || 'RAZENI PODLE cas';
}

/** Efektivní JS zdroj programu (dle režimu; konverze 2→3 přes kompilátor). */
export function programJs(p: CustomProgram): { js: string } | { error: string } {
  if (p.mode === 'js') {
    return p.js.trim() ? { js: p.js } : { error: 'JavaScript program je prázdný.' };
  }
  const expr = p.mode === 'expr' ? p.expr : visualToExpr(p);
  const valid = validateExpression(expr, p.kind);
  if (!valid.ok) return { error: valid.error };
  try {
    return { js: compileExpression(expr, p.kind).jsSource };
  } catch (e) {
    return { error: e instanceof Error ? e.message : 'Program se nepodařilo přeložit.' };
  }
}

/** Validace programů z cizích zdrojů; untrusted zahazuje surový JS režim. */
export function sanitizePrograms(raw: unknown, trusted: boolean): CustomProgram[] {
  if (!Array.isArray(raw)) return [];
  const out: CustomProgram[] = [];
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue;
    const p = item as Partial<CustomProgram>;
    if (typeof p.id !== 'string' || typeof p.name !== 'string') continue;
    if (p.kind !== 'filter' && p.kind !== 'sort' && p.kind !== 'search') continue;
    const mode: ProgramMode = p.mode === 'js' ? 'js' : p.mode === 'expr' ? 'expr' : 'visual';
    if (mode === 'js' && !trusted) continue; // spustitelný kód nepřebíráme z nedůvěryhodných zdrojů
    out.push({
      id: p.id,
      kind: p.kind,
      name: p.name.slice(0, 60),
      mode,
      visual:
        p.visual && typeof p.visual === 'object'
          ? {
              join: (p.visual as VisualSpec).join === 'NEBO' ? 'NEBO' : 'A',
              filter: Array.isArray((p.visual as VisualSpec).filter) ? (p.visual as VisualSpec).filter.slice(0, 20) : [],
              sort: Array.isArray((p.visual as VisualSpec).sort) ? (p.visual as VisualSpec).sort.slice(0, 10) : [],
              when: (p.visual as VisualSpec).when ?? null,
            }
          : { join: 'A', filter: [], sort: [], when: null },
      expr: typeof p.expr === 'string' ? p.expr.slice(0, 4000) : '',
      js: trusted && typeof p.js === 'string' ? p.js.slice(0, 20000) : '',
    });
  }
  return out.slice(0, 50);
}
